/**
 * Laparoscope close-up shared by the OR levels: a cached anatomy bitmap seen
 * through a round scope that the clerk pans and zooms, with fog, smoke, the
 * surgeons' instrument tips and "hold the camera here" commands.
 */
import {
  $,
  BAYER,
  C,
  type Cell,
  type Choice,
  clamp,
  closeZoom,
  type Ctx,
  DIRV,
  getImagery,
  type Hotspot,
  KEYMAP,
  type LapView,
  obox,
  openZoom,
  pell,
  pline,
  ptext,
  R,
  tr,
  type ZoomCfg,
} from "../../engine";
import { texturize, tissueCredit, tissueReady } from "./tissue";

export interface LapState extends LapView {
  fog: number;
  smoke: number;
  /** performance.now() of the previous drawn frame. */
  last: number;
}

export interface Pt {
  x: number;
  y: number;
}

/** The part of the world currently on screen. */
export interface Viewport {
  vw: number;
  vh: number;
  x0: number;
  y0: number;
}

export const newLap = (cx: number, cy: number, z = 1.3, fog = 0.15): LapState => ({
  cx,
  cy,
  z,
  fog,
  smoke: 0,
  pan: null,
  last: 0,
});

/* ---------- instruments ---------- */

export type TipKind = "grasp" | "suction" | "bipolar" | "scissors" | "hook" | "clip" | "bag";

export interface Tip {
  x: number;
  y: number;
  kind: TipKind;
  open?: boolean;
  water?: boolean;
  hold?: boolean;
  spark?: boolean;
  /** Something held in the jaws (dark red by default). */
  specimen?: boolean;
  specimenCol?: [edge: string, fill: string];
}

/** Paint both instrument shafts entering from the lower corners. Sparks add a little smoke. */
export function drawTools(
  c: Ctx,
  now: number,
  tips: { L: Tip; R: Tip },
  toS: (x: number, y: number) => Cell,
  lap: LapState,
): void {
  const tool = (side: "L" | "R", t: Tip): void => {
    const [sx, sy] = toS(t.x, t.y);
    const ox = side === "L" ? -4 : 196;
    const oy = 112;
    pline(c, ox, oy, sx, sy, "#3a3f52", 5);
    pline(c, ox, oy, sx, sy, "#b8bcc8", 3);
    const ang = Math.atan2(sy - oy, sx - ox);
    const px = -Math.sin(ang);
    const py = Math.cos(ang);
    const fx = Math.cos(ang);
    const fy = Math.sin(ang);
    if (t.kind === "suction") {
      obox(c, "#d9dcea", sx - 2, sy - 2, 4, 4);
      if (t.water && Math.floor(now / 120) % 2) R(c, "#9fd0ef", sx + 3, sy - 1, 3, 2);
      if (!t.water && Math.floor(now / 150) % 2) R(c, "#7a1020", sx - 1, sy + 3, 2, 2);
    } else if (t.kind === "bipolar" || t.kind === "hook") {
      if (t.kind === "bipolar") {
        const a = t.spark ? 1 : 2;
        pline(c, sx, sy, sx + fx * 5 + px * a, sy + fy * 5 + py * a, "#d9dcea", 1);
        pline(c, sx, sy, sx + fx * 5 - px * a, sy + fy * 5 - py * a, "#d9dcea", 1);
      } else {
        pline(c, sx, sy, sx + fx * 5, sy + fy * 5, "#d9dcea", 1);
        pline(c, sx + fx * 5, sy + fy * 5, sx + fx * 5 + px * 3, sy + fy * 5 + py * 3, "#d9dcea", 1);
      }
      if (t.spark) {
        R(c, "#ffffff", sx + fx * 6 - 1, sy + fy * 6 - 1, 3, 3);
        R(c, "#ffd447", sx + fx * 6 + 2, sy + fy * 6 - 2, 1, 1);
        R(c, "#ffd447", sx + fx * 6 - 3, sy + fy * 6 + 1, 1, 1);
        if (lap.smoke < 0.28) lap.smoke = Math.min(0.28, lap.smoke + 0.006);
      }
    } else if (t.kind === "scissors") {
      const a = t.open ? 3 : 1;
      pline(c, sx, sy, sx + fx * 6 + px * a, sy + fy * 6 + py * a, "#e6e9f5", 2);
      pline(c, sx, sy, sx + fx * 6 - px * a, sy + fy * 6 - py * a, "#e6e9f5", 2);
    } else if (t.kind === "clip") {
      const a = t.open ? 3 : 1;
      pline(c, sx, sy, sx + fx * 6 + px * a, sy + fy * 6 + py * a, "#c9ccd8", 2);
      pline(c, sx, sy, sx + fx * 6 - px * a, sy + fy * 6 - py * a, "#c9ccd8", 2);
      R(c, "#e8d070", sx + fx * 6 - 1, sy + fy * 6 - 1, 2, 2);
    } else if (t.kind === "bag") {
      pell(c, sx + fx * 7, sy + fy * 7, 6, 5, "#5a6aa8");
      pell(c, sx + fx * 7, sy + fy * 7, 5, 4, "#b8c8f0");
      if (t.specimen) pell(c, sx + fx * 7, sy + fy * 8, 3, 2, "#6a7a4a");
    } else {
      const a = t.open ? 3 : 1;
      pline(c, sx, sy, sx + fx * 5 + px * a, sy + fy * 5 + py * a, "#8a90a2", 2);
      pline(c, sx, sy, sx + fx * 5 - px * a, sy + fy * 5 - py * a, "#8a90a2", 2);
      if (t.specimen) {
        const [edge, fill] = t.specimenCol ?? ["#2e0c1c", "#4e1630"];
        pell(c, sx + fx * 6, sy + fy * 6, 4, 3, edge);
        pell(c, sx + fx * 6, sy + fy * 6, 3, 2, fill);
      }
    }
  };
  tool("L", tips.L);
  tool("R", tips.R);
}

/* ---------- camera commands ---------- */

export interface CamCmdDef<Tg extends string = string> {
  type: "look" | "out" | "wipe";
  tg?: Tg;
  minZ?: number;
  smoke?: boolean;
  text: string;
  /** Who gives the command (defaults to the level's choice). */
  who?: string;
}

export interface CamCmd<Tg extends string = string> extends CamCmdDef<Tg> {
  t: number;
  wiped?: boolean;
  wrongT?: number;
  wrongSaid?: boolean;
  hold?: number;
}

/**
 * Advance one frame of "hold the camera there". `decoy` is a target that counts
 * as the classic wrong side; `wrong` fires once after a second there, `hit`
 * after the view has been held on target for 0.6 s.
 */
export function trackCmd<Tg extends string>(
  cmd: CamCmd<Tg>,
  lap: LapState,
  dt: number,
  targets: Record<Tg, Pt>,
  decoy: Tg | null = null,
  radius = 16,
): { hit: boolean; wrong: boolean } {
  let ok: boolean;
  let wrong = false;
  if (cmd.type === "look" && cmd.tg) {
    const tg = targets[cmd.tg];
    ok =
      Math.hypot(lap.cx - tg.x, lap.cy - tg.y) < radius &&
      lap.fog < 0.5 &&
      lap.smoke < 0.4 &&
      (!cmd.minZ || lap.z >= cmd.minZ - 0.01);
    if (decoy) {
      const o = targets[decoy];
      if (Math.hypot(lap.cx - o.x, lap.cy - o.y) < radius) {
        cmd.wrongT = (cmd.wrongT ?? 0) + dt;
        if (cmd.wrongT > 1 && !cmd.wrongSaid) {
          cmd.wrongSaid = true;
          wrong = true;
        }
      }
    }
  } else if (cmd.type === "out") ok = lap.z <= 1.0;
  else ok = !!cmd.wiped && lap.fog < 0.15;
  let hit = false;
  if (ok) {
    cmd.hold = (cmd.hold ?? 0) + dt;
    hit = cmd.hold > 0.6;
  } else cmd.hold = 0;
  return { hit, wrong };
}

/* ---------- the scope ---------- */

export interface ScopeCfg {
  lap(): LapState;
  /** World bitmap size (default 300×150). */
  w?: number;
  h?: number;
  /** The world bitmap is repainted only when this key changes. */
  worldKey(): string;
  paintWorld(c: Ctx): void;
  title?: string;
  /** Live caption in the close-up header. */
  caption?(): string;
  tips?(now: number): { L: Tip; R: Tip } | null;
  /** Drawn over the anatomy, under fog/smoke and the round mask. */
  overlay?(c: Ctx, now: number, toS: (x: number, y: number) => Cell): void;
  /** Drawn on top of everything (zoom readout, progress pips…). */
  hud?(c: Ctx, now: number): void;
  hot?(): Hotspot[];
  /** Every frame, after drawing (dt in seconds). */
  onFrame?(dt: number): void;
  onWipe?(): void;
  hint?: string;
  /** Extra buttons after the camera controls. */
  extraBar?(): Choice[];
  /** Re-skin tissue with real photo textures when the player prefers photos (default true). */
  texture?: boolean;
  /** Exact colours the texture pass must leave as drawn (hidden gauze, thin vessels…). */
  textureKeep?: readonly string[];
}

export interface Scope {
  cfg: () => ZoomCfg;
  open: () => void;
  wipe: () => void;
  view: () => Viewport;
  /** World → close-up pixel coordinates. */
  toS: (x: number, y: number) => Cell;
  /** On screen and inside the round field of view. */
  inView: (p: Cell) => boolean;
  /** Drop the cached world bitmap (call from Level.reset). */
  reset: () => void;
}

const ZMIN = 0.85;
const ZMAX = 2;

/** Press-and-hold pan button for the zoom bar. */
export const panBtn = (label: string, hold: Cell): Choice => ({ label, hold, fn: () => undefined });

export function makeScope(o: ScopeCfg): Scope {
  const W = o.w ?? 300;
  const H = o.h ?? 150;
  let worldCv: HTMLCanvasElement | null = null;
  let worldKey = "";

  const textured = (): boolean => o.texture !== false && getImagery() === "photo";

  function world(): HTMLCanvasElement {
    const tex = textured();
    const k = `${o.worldKey()}|${tex ? (tissueReady() ? "tex" : "tex-loading") : "drawn"}`;
    if (worldCv && worldKey === k) return worldCv;
    const w = worldCv ?? document.createElement("canvas");
    w.width = W;
    w.height = H;
    const c = w.getContext("2d", { willReadFrequently: true });
    if (c) {
      o.paintWorld(c);
      if (tex) texturize(c, W, H, o.textureKeep ?? []);
    }
    worldCv = w;
    worldKey = k;
    return w;
  }

  function view(): Viewport {
    const lp = o.lap();
    const vw = 160 / lp.z;
    const vh = 90 / lp.z;
    lp.cx = clamp(lp.cx, vw / 2, W - vw / 2);
    lp.cy = clamp(lp.cy, vh / 2, H - vh / 2);
    return { vw, vh, x0: lp.cx - vw / 2, y0: lp.cy - vh / 2 };
  }

  function toS(x: number, y: number): Cell {
    const v = view();
    return [((x - v.x0) / v.vw) * 192, ((y - v.y0) / v.vh) * 108];
  }

  const inView = ([sx, sy]: Cell): boolean =>
    sx > 8 && sx < 184 && sy > 6 && sy < 102 && (sx - 96) * (sx - 96) + (sy - 54) * (sy - 54) < 64 * 64;

  function wipe(): void {
    o.lap().fog = 0.04;
    o.onWipe?.();
  }

  function draw(c: Ctx, now: number): void {
    const lp = o.lap();
    const dt = lp.last ? Math.min(0.1, (now - lp.last) / 1000) : 0;
    lp.last = now;
    lp.fog = Math.min(0.85, lp.fog + dt * 0.02);
    lp.smoke = Math.max(0, lp.smoke - dt * 0.08);
    if (lp.pan) {
      lp.cx += (lp.pan[0] * dt * 60) / lp.z;
      lp.cy += (lp.pan[1] * dt * 60) / lp.z;
    }
    const v = view();
    const sc = (x: number, y: number): Cell => [((x - v.x0) / v.vw) * 192, ((y - v.y0) / v.vh) * 108];
    c.fillStyle = "#000";
    c.fillRect(0, 0, 192, 108);
    c.imageSmoothingEnabled = false;
    c.drawImage(world(), v.x0, v.y0, v.vw, v.vh, 0, 0, 192, 108);
    const tips = o.tips?.(now);
    if (tips) drawTools(c, now, tips, sc, lp);
    o.overlay?.(c, now, sc);
    const img = c.getImageData(0, 0, 192, 108);
    const d = img.data;
    const fog = lp.fog * 0.9;
    const smk = lp.smoke * 0.9;
    for (let y = 0; y < 108; y++)
      for (let x = 0; x < 192; x++) {
        const i = (y * 192 + x) * 4;
        const dx = x - 96;
        const dy = y - 54;
        const r2 = dx * dx + dy * dy;
        if (r2 > 66 * 66) {
          d[i] = d[i + 1] = d[i + 2] = 0;
          continue;
        }
        const b = (BAYER[(y & 3) * 4 + (x & 3)] ?? 0) / 16;
        if (smk > b) {
          d[i] = 150;
          d[i + 1] = 150;
          d[i + 2] = 156;
        } else if (fog > b) {
          d[i] = 228;
          d[i + 1] = 232;
          d[i + 2] = 240;
        } else if (r2 > 60 * 60 && b < 0.5) {
          d[i] = (d[i] ?? 0) * 0.5;
          d[i + 1] = (d[i + 1] ?? 0) * 0.5;
          d[i + 2] = (d[i + 2] ?? 0) * 0.5;
        }
      }
    c.putImageData(img, 0, 0);
    R(c, "#ffffff", 95, 53, 2, 2);
    ptext(c, "x" + lp.z.toFixed(1), 4, 100, "#7fe08a");
    o.hud?.(c, now);
    const credit = textured() ? tissueCredit() : "";
    if (o.caption || credit) {
      const sub = $("#zoom .zh span");
      const want = [o.caption?.() ?? tr("你扶著鏡頭", "You’re on the camera"), credit]
        .filter(Boolean)
        .join(tr("　", " · "));
      if (sub.textContent !== want) sub.textContent = want;
    }
    o.onFrame?.(dt);
  }

  function cfg(): ZoomCfg {
    return {
      title: o.title ?? tr("腹腔鏡畫面", "Laparoscope view"),
      sub: tr("你扶著鏡頭", "You’re on the camera"),
      photoToggle: o.texture !== false,
      draw,
      hot: () => o.hot?.() ?? [],
      hint:
        o.hint ??
        tr(
          "方向鍵移動　Z／X 拉近拉遠　C 擦鏡頭　可拖曳",
          "Arrows: pan · Z/X: zoom in/out · C: wipe lens · drag to move",
        ),
      bar: () => [
        panBtn("◀", [-1, 0]),
        panBtn("▶", [1, 0]),
        panBtn("▲", [0, -1]),
        panBtn("▼", [0, 1]),
        C(tr("拉近", "Zoom in"), () => {
          const lp = o.lap();
          lp.z = clamp(lp.z + 0.15, ZMIN, ZMAX);
        }),
        C(tr("拉遠", "Zoom out"), () => {
          const lp = o.lap();
          lp.z = clamp(lp.z - 0.15, ZMIN, ZMAX);
        }),
        C(tr("擦鏡頭", "Wipe lens"), wipe),
        ...(o.extraBar?.() ?? []),
      ],
      onKey(e) {
        const lp = o.lap();
        const st = 8 / lp.z;
        const m = KEYMAP[e.key];
        if (m) {
          const [vx, vy] = DIRV[m];
          lp.cx += vx * st;
          lp.cy += vy * st;
          return true;
        }
        if (e.key === "z" || e.key === "Z" || e.key === "+" || e.key === "=") {
          lp.z = clamp(lp.z + 0.1, ZMIN, ZMAX);
          return true;
        }
        if (e.key === "x" || e.key === "X" || e.key === "-") {
          lp.z = clamp(lp.z - 0.1, ZMIN, ZMAX);
          return true;
        }
        if (e.key === "c" || e.key === "C") {
          wipe();
          return true;
        }
        return false;
      },
      onDrag(dx, dy) {
        const lp = o.lap();
        lp.cx -= (dx * (160 / lp.z)) / 192;
        lp.cy -= (dy * (90 / lp.z)) / 108;
      },
      onWheel(dd) {
        const lp = o.lap();
        lp.z = clamp(lp.z + (dd < 0 ? 0.08 : -0.08), ZMIN, ZMAX);
      },
    };
  }

  return {
    cfg,
    open: () => {
      closeZoom();
      openZoom(cfg());
    },
    wipe,
    view,
    toS,
    inView,
    reset: () => {
      worldCv = null;
      worldKey = "";
    },
  };
}
